// src/pages/legal/TermsConditions.tsx

import React from "react";
import { Link } from "react-router-dom";

import "./LegalPages.css";

// =====================================================
// TERMS & CONDITIONS
// =====================================================

const TermsConditions: React.FC = () => {
  return (
    <main className="legal-page">

      {/* ================================================= */}
      {/* HERO                                              */}
      {/* ================================================= */}

      <section className="legal-hero">

        <div className="legal-hero-glow" />

        <div className="legal-hero-content">

          {/* ============================================= */}
          {/* BREADCRUMB                                    */}
          {/* ============================================= */}

          <nav
            className="legal-breadcrumb"
            aria-label="Breadcrumb"
          >
            <Link to="/">
              P-ARENA
            </Link>

            <span>/</span>

            <span>
              Legal
            </span>

            <span>/</span>

            <strong aria-current="page">
              Terms &amp; Conditions
            </strong>
          </nav>

          {/* ============================================= */}
          {/* EMBLEM                                        */}
          {/* ============================================= */}

          <div className="legal-hero-emblem">
            ♠
          </div>

          <p className="legal-eyebrow">
            P-ARENA LEGAL
          </p>

          <h1>
            Terms &amp; Conditions
          </h1>

          <div className="legal-divider">
            <span />
            <strong>◆</strong>
            <span />
          </div>

          <p className="legal-hero-description">
            These Terms &amp; Conditions govern your
            access to and use of P-ARENA, including
            accounts, multiplayer games, AI games,
            social features, and other services
            provided through the platform.
          </p>

          <div className="legal-last-updated">
            Last Updated: August 2026
          </div>

        </div>

      </section>

      {/* ================================================= */}
      {/* SECTION NAVIGATION                                */}
      {/* ================================================= */}

      <nav
        className="legal-section-nav"
        aria-label="Terms and Conditions sections"
      >
        <div className="legal-section-nav-inner">

          <a href="#agreement">
            Agreement
          </a>

          <a href="#eligibility">
            Eligibility
          </a>

          <a href="#accounts">
            Accounts
          </a>

          <a href="#games">
            Games
          </a>

          <a href="#conduct">
            Conduct
          </a>

          <a href="#fair-play">
            Fair Play
          </a>

          <a href="#content">
            Content
          </a>

          <a href="#availability">
            Availability
          </a>

          <a href="#liability">
            Liability
          </a>

          <a href="#termination">
            Termination
          </a>

          <a href="#changes">
            Changes
          </a>

          <a href="#contact">
            Contact
          </a>

        </div>
      </nav>

      {/* ================================================= */}
      {/* CONTENT                                           */}
      {/* ================================================= */}

      <div className="legal-content">

        {/* ================================================= */}
        {/* AGREEMENT                                         */}
        {/* ================================================= */}

        <section
          id="agreement"
          className="legal-section"
        >

          <div className="legal-section-header">

            <span className="legal-section-number">
              01
            </span>

            <div>
              <p>
                YOUR AGREEMENT
              </p>

              <h2>
                Acceptance of These Terms
              </h2>
            </div>

          </div>

          <div className="legal-copy">

            <p>
              Welcome to{" "}
              <strong>P-ARENA</strong>.
              These Terms &amp; Conditions
              ("Terms") govern your access to and
              use of the P-ARENA platform,
              including its website, games,
              accounts, multiplayer features,
              artificial intelligence opponents,
              social features, and related
              services.
            </p>

            <p>
              By creating an account, accessing
              P-ARENA, or using any part of the
              platform, you agree to be bound by
              these Terms.
            </p>

            <p>
              If you do not agree with these
              Terms, you should not access or use
              P-ARENA.
            </p>

            <p>
              Your use of P-ARENA is also subject
              to our{" "}
              <Link
                to="/privacy"
                className="legal-inline-link"
              >
                Privacy Policy
              </Link>{" "}
              and{" "}
              <Link
                to="/cookies"
                className="legal-inline-link"
              >
                Cookie Policy
              </Link>.
            </p>

          </div>

        </section>

        {/* ================================================= */}
        {/* ELIGIBILITY                                       */}
        {/* ================================================= */}

        <section
          id="eligibility"
          className="legal-section"
        >

          <div className="legal-section-header">

            <span className="legal-section-number">
              02
            </span>

            <div>
              <p>
                ACCESS
              </p>

              <h2>
                Eligibility
              </h2>
            </div>

          </div>

          <div className="legal-copy">

            <p>
              You may use P-ARENA only if you are
              legally permitted to use the
              platform under the laws applicable
              to you.
            </p>

            <p>
              If you are not legally able to agree
              to these Terms on your own, your use
              of P-ARENA must be authorized by a
              parent, legal guardian, or other
              person legally permitted to provide
              such authorization where required.
            </p>

            <p>
              You are responsible for ensuring
              that your use of P-ARENA complies
              with applicable laws and
              regulations.
            </p>

          </div>

        </section>

        {/* ================================================= */}
        {/* ACCOUNTS                                          */}
        {/* ================================================= */}

        <section
          id="accounts"
          className="legal-section"
        >

          <div className="legal-section-header">

            <span className="legal-section-number">
              03
            </span>

            <div>
              <p>
                YOUR PROFILE
              </p>

              <h2>
                Accounts &amp; Registration
              </h2>
            </div>

          </div>

          <div className="legal-copy">

            <p>
              Certain P-ARENA features require a
              registered account. When creating
              an account, you agree to provide
              information that is accurate and
              reasonably current.
            </p>

            <p>
              You are responsible for maintaining
              the confidentiality of your account
              credentials and for activity
              occurring through your account,
              except where responsibility cannot
              legally be assigned to you.
            </p>

          </div>

          <div className="legal-card-grid">

            <article className="legal-info-card">

              <span className="legal-card-number">
                01
              </span>

              <h3>
                Account Information
              </h3>

              <p>
                Keep your account information
                accurate and current.
              </p>

              <ul>
                <li>
                  Use valid registration information
                </li>

                <li>
                  Maintain an accurate email address
                </li>

                <li>
                  Update information when necessary
                </li>
              </ul>

            </article>

            <article className="legal-info-card">

              <span className="legal-card-number">
                02
              </span>

              <h3>
                Account Security
              </h3>

              <p>
                Take reasonable measures to protect
                your account and credentials.
              </p>

              <ul>
                <li>
                  Protect your password
                </li>

                <li>
                  Do not knowingly share credentials
                  improperly
                </li>

                <li>
                  Report suspected unauthorized
                  access
                </li>
              </ul>

            </article>

            <article className="legal-info-card">

              <span className="legal-card-number">
                03
              </span>

              <h3>
                One User, One Identity
              </h3>

              <p>
                Accounts should represent the user
                who registered them and should not
                be used to impersonate another
                person.
              </p>

              <ul>
                <li>
                  No impersonation
                </li>

                <li>
                  No misleading identity information
                </li>

                <li>
                  No unauthorized account access
                </li>
              </ul>

            </article>

            <article className="legal-info-card">

              <span className="legal-card-number">
                04
              </span>

              <h3>
                Account Activity
              </h3>

              <p>
                P-ARENA may maintain information
                associated with gameplay and
                account activity.
              </p>

              <ul>
                <li>
                  Games played
                </li>

                <li>
                  Wins and losses
                </li>

                <li>
                  Scores
                </li>

                <li>
                  Player statistics
                </li>
              </ul>

            </article>

          </div>

        </section>

        {/* ================================================= */}
        {/* GAMES                                             */}
        {/* ================================================= */}

        <section
          id="games"
          className="legal-section"
        >

          <div className="legal-section-header">

            <span className="legal-section-number">
              04
            </span>

            <div>
              <p>
                GAMEPLAY
              </p>

              <h2>
                P-ARENA Games
              </h2>
            </div>

          </div>

          <div className="legal-copy">

            <p>
              P-ARENA is a social card-game
              platform designed to allow users to
              play supported games against friends,
              online players, or computer-controlled
              opponents where those modes are
              available.
            </p>

            <p>
              Individual games may have their own
              rules, scoring systems, gameplay
              mechanics, and victory conditions.
              Those rules form part of the
              gameplay experience.
            </p>

            <p>
              P-ARENA may add, remove, modify, or
              temporarily disable games, game
              modes, rules, scoring systems, or
              other gameplay features as the
              platform develops.
            </p>

          </div>

          <div className="legal-highlight">

            <span className="legal-highlight-icon">
              ♠
            </span>

            <div>

              <small>
                GAMEPLAY
              </small>

              <h3>
                Play • Connect • Compete
              </h3>

              <p>
                P-ARENA is designed around social
                card-game competition, including
                games with friends, online players,
                and AI opponents where supported.
              </p>

            </div>

          </div>

        </section>

        {/* ================================================= */}
        {/* PLAYER CONDUCT                                    */}
        {/* ================================================= */}

        <section
          id="conduct"
          className="legal-section"
        >

          <div className="legal-section-header">

            <span className="legal-section-number">
              05
            </span>

            <div>
              <p>
                COMMUNITY
              </p>

              <h2>
                Player Conduct
              </h2>
            </div>

          </div>

          <div className="legal-copy">

            <p>
              Users are expected to interact with
              P-ARENA and other players in a
              lawful and respectful manner.
            </p>

            <p>
              You agree not to misuse the platform
              or interfere with another person's
              ability to use or enjoy P-ARENA.
            </p>

          </div>

          <div className="legal-purpose-list">

            <article>

              <span>
                01
              </span>

              <div>

                <h3>
                  Respect Other Players
                </h3>

                <p>
                  Do not use P-ARENA to threaten,
                  harass, abuse, or intentionally
                  target other users in violation
                  of applicable rules or law.
                </p>

              </div>

            </article>

            <article>

              <span>
                02
              </span>

              <div>

                <h3>
                  Do Not Disrupt the Platform
                </h3>

                <p>
                  Do not intentionally interfere
                  with servers, game sessions,
                  matchmaking, accounts, networks,
                  or other P-ARENA functionality.
                </p>

              </div>

            </article>

            <article>

              <span>
                03
              </span>

              <div>

                <h3>
                  No Unauthorized Access
                </h3>

                <p>
                  Do not attempt to access another
                  user's account, private data,
                  administrative systems, or other
                  restricted portions of P-ARENA
                  without authorization.
                </p>

              </div>

            </article>

            <article>

              <span>
                04
              </span>

              <div>

                <h3>
                  Follow Applicable Law
                </h3>

                <p>
                  P-ARENA must not be used for
                  unlawful activity or in a manner
                  that violates applicable legal
                  requirements.
                </p>

              </div>

            </article>

          </div>

        </section>

        {/* ================================================= */}
        {/* FAIR PLAY                                         */}
        {/* ================================================= */}

        <section
          id="fair-play"
          className="legal-section"
        >

          <div className="legal-section-header">

            <span className="legal-section-number">
              06
            </span>

            <div>
              <p>
                COMPETITION
              </p>

              <h2>
                Fair Play &amp; Game Integrity
              </h2>
            </div>

          </div>

          <div className="legal-copy">

            <p>
              Fair competition is an important part
              of the P-ARENA experience. You agree
              not to intentionally manipulate,
              exploit, or interfere with gameplay
              in a manner that provides an
              unauthorized advantage.
            </p>

          </div>

          <div className="legal-card-grid">

            <article className="legal-info-card">

              <span className="legal-card-number">
                01
              </span>

              <h3>
                No Cheating
              </h3>

              <p>
                Do not use unauthorized tools,
                software, scripts, or techniques
                designed to create an unfair
                gameplay advantage.
              </p>

            </article>

            <article className="legal-info-card">

              <span className="legal-card-number">
                02
              </span>

              <h3>
                No Exploitation
              </h3>

              <p>
                Do not knowingly exploit bugs,
                vulnerabilities, or unintended
                platform behavior to manipulate
                games or statistics.
              </p>

            </article>

            <article className="legal-info-card">

              <span className="legal-card-number">
                03
              </span>

              <h3>
                No Match Manipulation
              </h3>

              <p>
                Do not improperly manipulate
                matches, scores, rankings, wins,
                losses, or other player statistics.
              </p>

            </article>

            <article className="legal-info-card">

              <span className="legal-card-number">
                04
              </span>

              <h3>
                Report Problems
              </h3>

              <p>
                If you discover a gameplay bug or
                security issue, we encourage you
                to report it rather than knowingly
                exploiting it.
              </p>

            </article>

          </div>

        </section>

        {/* ================================================= */}
        {/* SOCIAL FEATURES                                   */}
        {/* ================================================= */}

        <section className="legal-section">

          <div className="legal-section-header">

            <span className="legal-section-number">
              07
            </span>

            <div>
              <p>
                CONNECTIONS
              </p>

              <h2>
                Friends &amp; Multiplayer Features
              </h2>
            </div>

          </div>

          <div className="legal-copy">

            <p>
              P-ARENA may provide features that
              allow users to connect with friends,
              find opponents, participate in
              multiplayer games, and interact with
              other registered players.
            </p>

            <p>
              You are responsible for how you use
              social and multiplayer features and
              for the information you choose to
              make available to other users through
              those features.
            </p>

            <p>
              P-ARENA may restrict access to social
              or multiplayer functionality where
              reasonably necessary to protect the
              platform, enforce these Terms, or
              address abuse.
            </p>

          </div>

        </section>

        {/* ================================================= */}
        {/* AI                                                */}
        {/* ================================================= */}

        <section className="legal-section">

          <div className="legal-section-header">

            <span className="legal-section-number">
              08
            </span>

            <div>
              <p>
                COMPUTER OPPONENTS
              </p>

              <h2>
                Playing Against AI
              </h2>
            </div>

          </div>

          <div className="legal-copy">

            <p>
              Certain P-ARENA games may allow you
              to play against a computer-controlled
              or artificial intelligence opponent.
            </p>

            <p>
              AI opponents are software-controlled
              game participants and are not human
              users.
            </p>

            <p>
              The behavior, difficulty, strategy,
              names, avatars, or other
              characteristics of AI opponents may
              be modified as P-ARENA develops.
            </p>

          </div>

          <div className="legal-note">

            <strong>
              ◆
            </strong>

            <p>
              An AI opponent's displayed name or
              avatar is part of the game experience
              and does not mean that the opponent
              represents a real person.
            </p>

          </div>

        </section>

        {/* ================================================= */}
        {/* CONTENT                                           */}
        {/* ================================================= */}

        <section
          id="content"
          className="legal-section"
        >

          <div className="legal-section-header">

            <span className="legal-section-number">
              09
            </span>

            <div>
              <p>
                OWNERSHIP
              </p>

              <h2>
                Platform Content &amp; Intellectual Property
              </h2>
            </div>

          </div>

          <div className="legal-copy">

            <p>
              P-ARENA and its platform materials,
              including software, interfaces,
              designs, branding, graphics,
              platform-specific content, and
              related materials, may be protected
              by intellectual property and other
              applicable laws.
            </p>

            <p>
              Except as permitted by law or with
              appropriate authorization, you may
              not copy, reproduce, distribute,
              modify, sell, license, reverse
              engineer, or commercially exploit
              protected P-ARENA materials.
            </p>

            <p>
              These Terms do not transfer ownership
              of P-ARENA intellectual property to
              you.
            </p>

          </div>

        </section>

        {/* ================================================= */}
        {/* USER CONTENT                                      */}
        {/* ================================================= */}

        <section className="legal-section">

          <div className="legal-section-header">

            <span className="legal-section-number">
              10
            </span>

            <div>
              <p>
                USER MATERIAL
              </p>

              <h2>
                Information You Provide
              </h2>
            </div>

          </div>

          <div className="legal-copy">

            <p>
              P-ARENA may allow users to provide
              profile information, avatars, bios,
              reports, messages, or other
              information through supported
              features.
            </p>

            <p>
              You are responsible for information
              and materials you choose to provide
              and should not submit content that
              you do not have the right to use.
            </p>

            <p>
              You must not knowingly submit
              unlawful, malicious, infringing, or
              deceptive content through the
              platform.
            </p>

          </div>

        </section>

        {/* ================================================= */}
        {/* AVAILABILITY                                      */}
        {/* ================================================= */}

        <section
          id="availability"
          className="legal-section"
        >

          <div className="legal-section-header">

            <span className="legal-section-number">
              11
            </span>

            <div>
              <p>
                SERVICE OPERATION
              </p>

              <h2>
                Availability &amp; Platform Changes
              </h2>
            </div>

          </div>

          <div className="legal-copy">

            <p>
              We aim to provide a reliable
              P-ARENA experience, but continuous
              or uninterrupted availability cannot
              be guaranteed.
            </p>

            <p>
              P-ARENA may occasionally be
              unavailable because of maintenance,
              updates, technical problems,
              infrastructure issues, security
              events, or circumstances outside
              reasonable control.
            </p>

            <p>
              Features, games, interfaces,
              multiplayer systems, AI behavior,
              scoring systems, or other platform
              functionality may change over time.
            </p>

          </div>

        </section>

        {/* ================================================= */}
        {/* GAME DATA                                         */}
        {/* ================================================= */}

        <section className="legal-section">

          <div className="legal-section-header">

            <span className="legal-section-number">
              12
            </span>

            <div>
              <p>
                STATISTICS
              </p>

              <h2>
                Scores, Wins &amp; Game Records
              </h2>
            </div>

          </div>

          <div className="legal-copy">

            <p>
              P-ARENA may maintain game-related
              information such as scores, matches,
              games played, wins, losses, draws,
              ratings, rankings, and other
              statistics.
            </p>

            <p>
              Technical errors, interrupted
              connections, software defects, or
              other circumstances may occasionally
              affect game results or statistics.
            </p>

            <p>
              Where reasonably necessary to
              maintain platform integrity, P-ARENA
              may investigate and correct game
              records affected by confirmed
              technical errors, abuse, cheating,
              or manipulation.
            </p>

          </div>

        </section>

        {/* ================================================= */}
        {/* NO GAMBLING                                       */}
        {/* ================================================= */}

        <section className="legal-section">

          <div className="legal-section-header">

            <span className="legal-section-number">
              13
            </span>

            <div>
              <p>
                ENTERTAINMENT
              </p>

              <h2>
                Recreational Gameplay
              </h2>
            </div>

          </div>

          <div className="legal-highlight">

            <span className="legal-highlight-icon">
              ♦
            </span>

            <div>

              <small>
                P-ARENA GAMES
              </small>

              <h3>
                Games Are for Entertainment
              </h3>

              <p>
                Unless P-ARENA expressly introduces
                a separately governed feature,
                standard P-ARENA card games,
                scores, wins, rankings, and game
                points are intended for
                entertainment and competitive
                gameplay within the platform.
              </p>

            </div>

          </div>

          <div className="legal-copy">

            <p>
              P-ARENA game points and ordinary
              gameplay statistics do not represent
              money, legal tender, or a promise of
              monetary value.
            </p>

            <p>
              Users must not use P-ARENA in a
              manner that violates applicable
              gambling, gaming, or other laws.
            </p>

          </div>

        </section>

        {/* ================================================= */}
        {/* SECURITY                                          */}
        {/* ================================================= */}

        <section className="legal-section">

          <div className="legal-section-header">

            <span className="legal-section-number">
              14
            </span>

            <div>
              <p>
                PLATFORM PROTECTION
              </p>

              <h2>
                Security &amp; Technical Misuse
              </h2>
            </div>

          </div>

          <div className="legal-copy">

            <p>
              You must not intentionally introduce
              malicious code, interfere with
              security controls, probe systems
              without authorization, or attempt to
              gain unauthorized access to P-ARENA
              systems, accounts, databases,
              networks, or infrastructure.
            </p>

            <p>
              Automated access, scraping,
              excessive requests, bots, scripts,
              or similar technologies may not be
              used in a manner that interferes
              with normal platform operation or
              violates these Terms.
            </p>

          </div>

        </section>

        {/* ================================================= */}
        {/* THIRD PARTY                                       */}
        {/* ================================================= */}

        <section className="legal-section">

          <div className="legal-section-header">

            <span className="legal-section-number">
              15
            </span>

            <div>
              <p>
                EXTERNAL SERVICES
              </p>

              <h2>
                Third-Party Services
              </h2>
            </div>

          </div>

          <div className="legal-copy">

            <p>
              P-ARENA may rely on or interact with
              third-party services, infrastructure,
              hosting providers, authentication
              technologies, communications
              services, or other external systems.
            </p>

            <p>
              Third-party services may be governed
              by their own terms and privacy
              policies.
            </p>

            <p>
              To the extent permitted by law,
              P-ARENA is not responsible for
              third-party services that are not
              operated or controlled by P-ARENA.
            </p>

          </div>

        </section>

        {/* ================================================= */}
        {/* DISCLAIMER                                        */}
        {/* ================================================= */}

        <section className="legal-section">

          <div className="legal-section-header">

            <span className="legal-section-number">
              16
            </span>

            <div>
              <p>
                SERVICE DISCLAIMER
              </p>

              <h2>
                Platform Provided "As Is"
              </h2>
            </div>

          </div>

          <div className="legal-copy">

            <p>
              To the maximum extent permitted by
              applicable law, P-ARENA is provided
              on an "as is" and "as available"
              basis.
            </p>

            <p>
              We do not guarantee that the
              platform will always be uninterrupted,
              error-free, secure, or free from
              defects.
            </p>

            <p>
              Nothing in these Terms excludes any
              warranty, right, or protection that
              cannot legally be excluded under
              applicable law.
            </p>

          </div>

        </section>

        {/* ================================================= */}
        {/* LIABILITY                                         */}
        {/* ================================================= */}

        <section
          id="liability"
          className="legal-section"
        >

          <div className="legal-section-header">

            <span className="legal-section-number">
              17
            </span>

            <div>
              <p>
                RESPONSIBILITY
              </p>

              <h2>
                Limitation of Liability
              </h2>
            </div>

          </div>

          <div className="legal-copy">

            <p>
              To the maximum extent permitted by
              applicable law, P-ARENA and its
              operators will not be liable for
              indirect, incidental, special,
              consequential, or similar damages
              arising from or relating to use of,
              or inability to use, the platform.
            </p>

            <p>
              This may include, where permitted by
              law, losses associated with
              interrupted gameplay, unavailable
              services, loss of game progress,
              technical errors, or unauthorized
              activity outside P-ARENA's reasonable
              control.
            </p>

            <p>
              Some jurisdictions do not permit
              certain exclusions or limitations of
              liability. In those jurisdictions,
              these provisions apply only to the
              extent permitted by law.
            </p>

          </div>

          <div className="legal-note">

            <strong>
              ◆
            </strong>

            <p>
              Nothing in these Terms is intended
              to limit liability or consumer
              rights where doing so would be
              prohibited by applicable law.
            </p>

          </div>

        </section>

        {/* ================================================= */}
        {/* TERMINATION                                       */}
        {/* ================================================= */}

        <section
          id="termination"
          className="legal-section"
        >

          <div className="legal-section-header">

            <span className="legal-section-number">
              18
            </span>

            <div>
              <p>
                ACCOUNT ACCESS
              </p>

              <h2>
                Suspension &amp; Termination
              </h2>
            </div>

          </div>

          <div className="legal-copy">

            <p>
              P-ARENA may restrict, suspend, or
              terminate access to some or all
              platform features where reasonably
              necessary to protect users, maintain
              security, enforce these Terms,
              comply with law, or respond to
              serious or repeated misuse.
            </p>

            <p>
              Examples may include cheating,
              unauthorized access, deliberate
              exploitation of platform
              vulnerabilities, abusive behavior,
              manipulation of game records, or
              other material violations of these
              Terms.
            </p>

            <p>
              Where appropriate and reasonably
              possible, P-ARENA may provide notice
              or an opportunity to address an
              account-related issue.
            </p>

          </div>

        </section>

        {/* ================================================= */}
        {/* PRIVACY                                           */}
        {/* ================================================= */}

        <section className="legal-section">

          <div className="legal-section-header">

            <span className="legal-section-number">
              19
            </span>

            <div>
              <p>
                YOUR INFORMATION
              </p>

              <h2>
                Privacy
              </h2>
            </div>

          </div>

          <div className="legal-copy">

            <p>
              Information associated with your use
              of P-ARENA is handled in accordance
              with our applicable privacy
              practices.
            </p>

            <p>
              Please review the{" "}
              <Link
                to="/privacy"
                className="legal-inline-link"
              >
                Privacy Policy
              </Link>{" "}
              for information about how P-ARENA
              collects, uses, stores, and protects
              information.
            </p>

            <p>
              You can also review the{" "}
              <Link
                to="/cookies"
                className="legal-inline-link"
              >
                Cookie Policy
              </Link>{" "}
              for information about cookies,
              browser storage, and similar
              technologies.
            </p>

          </div>

        </section>

        {/* ================================================= */}
        {/* CHANGES                                           */}
        {/* ================================================= */}

        <section
          id="changes"
          className="legal-section"
        >

          <div className="legal-section-header">

            <span className="legal-section-number">
              20
            </span>

            <div>
              <p>
                UPDATES
              </p>

              <h2>
                Changes to These Terms
              </h2>
            </div>

          </div>

          <div className="legal-copy">

            <p>
              P-ARENA may update these Terms from
              time to time to reflect changes to
              the platform, games, services,
              security practices, business
              operations, or applicable legal
              requirements.
            </p>

            <p>
              When these Terms are updated, the
              "Last Updated" date at the top of
              this page will be revised.
            </p>

            <p>
              Where required by applicable law,
              additional notice or consent may be
              provided before material changes
              become effective.
            </p>

            <p>
              Your continued use of P-ARENA after
              updated Terms become effective
              constitutes acceptance of those
              updated Terms to the extent
              permitted by applicable law.
            </p>

          </div>

        </section>

        {/* ================================================= */}
        {/* SEVERABILITY                                      */}
        {/* ================================================= */}

        <section className="legal-section">

          <div className="legal-section-header">

            <span className="legal-section-number">
              21
            </span>

            <div>
              <p>
                GENERAL TERMS
              </p>

              <h2>
                Severability
              </h2>
            </div>

          </div>

          <div className="legal-copy">

            <p>
              If any provision of these Terms is
              found to be invalid, unlawful, or
              unenforceable, the remaining
              provisions will continue to apply to
              the extent permitted by law.
            </p>

            <p>
              An unenforceable provision may be
              interpreted or limited to the
              minimum extent necessary to make it
              enforceable where permitted by law.
            </p>

          </div>

        </section>

        {/* ================================================= */}
        {/* ENTIRE AGREEMENT                                  */}
        {/* ================================================= */}

        <section className="legal-section">

          <div className="legal-section-header">

            <span className="legal-section-number">
              22
            </span>

            <div>
              <p>
                AGREEMENT
              </p>

              <h2>
                Entire Agreement
              </h2>
            </div>

          </div>

          <div className="legal-copy">

            <p>
              These Terms, together with policies
              and other terms expressly
              incorporated into them, constitute
              the agreement governing your use of
              P-ARENA, subject to any additional
              terms that may apply to specific
              features or services.
            </p>

          </div>

        </section>

        {/* ================================================= */}
        {/* CONTACT                                           */}
        {/* ================================================= */}

        <section
          id="contact"
          className="legal-section"
        >

          <div className="legal-section-header">

            <span className="legal-section-number">
              23
            </span>

            <div>
              <p>
                QUESTIONS
              </p>

              <h2>
                Contact Us
              </h2>
            </div>

          </div>

          <div className="legal-contact-card">

            <div className="legal-contact-emblem">
              ♠ ♥ ♦ ♣
            </div>

            <small>
              P-ARENA
            </small>

            <h3>
              Questions About These Terms?
            </h3>

            <p>
              If you have questions about these
              Terms &amp; Conditions, your account,
              or your use of P-ARENA, please
              contact us through the contact
              information provided on the
              platform.
            </p>

          </div>

        </section>

        {/* ================================================= */}
        {/* LEGAL NAVIGATION                                  */}
        {/* ================================================= */}

        <section className="legal-footer-navigation">

          <div className="legal-footer-suits">
            <span>♠</span>
            <span>♥</span>
            <span>♦</span>
            <span>♣</span>
          </div>

          <small>
            P-ARENA
          </small>

          <h2>
            Legal Information
          </h2>

          <p>
            Review the policies governing your
            access to and use of P-ARENA.
          </p>

          <div className="legal-footer-links">

            <Link to="/privacy">
              Privacy Policy
            </Link>

            <Link
              to="/terms"
              className="active"
            >
              Terms &amp; Conditions
            </Link>

            <Link to="/cookies">
              Cookie Policy
            </Link>

          </div>

          <Link
            to="/"
            className="legal-back-home"
          >
            ← Back to Game House
          </Link>

        </section>

      </div>

    </main>
  );
};

export default TermsConditions;